import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CalendarPlus,
  BellRing,
  MapPin,
  CalendarDays,
  Ticket,
  ChevronDown,
  ExternalLink,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { googleCalendarUrl, downloadIcs } from "@/lib/calendar";
import { fmtDate, fmtRange } from "@/lib/format";
import { Countdown } from "./Countdown";

const CAT = {
  festival: { label: "Festival", color: "#00E676" },
  concert: { label: "Concert", color: "#A855F7" },
  sport: { label: "Sport & Fit", color: "#FF6B00" },
  club: { label: "Rave & Indoor", color: "#00E5FF" },
  voetbal: { label: "Voetbal", color: "#FFC53D" },
};

export const EventCard = ({ event, index, onRemind }) => {
  const isLive = event.status === "live";
  const isExpected = event.status === "verwacht";
  const gUrl = googleCalendarUrl(event);
  const badge = CAT[event.category] || CAT.festival;
  const badgeLabel = event.genre || badge.label;
  const detailUrl = `/event/${event.slug}`;

  return (
    <motion.article
      data-testid={`event-card-${event.id}`}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className="group flex flex-col overflow-hidden rounded-2xl border border-white/[0.08] bg-[#12121C] transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1.5 hover:border-[rgba(0,230,118,0.4)] hover:shadow-[0_20px_60px_-20px_rgba(0,230,118,0.25)]"
    >
      <Link to={detailUrl} data-testid={`event-image-link-${event.id}`} className="relative block h-44 shrink-0 overflow-hidden">
        {event.image_url && (
          <img
            src={event.image_url}
            alt={event.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#12121C] via-[#12121C]/25 to-transparent" />
        <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-black/70 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest backdrop-blur" style={{ color: badge.color }}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: badge.color }} />
          {badgeLabel}
        </span>
        {event.status === "afgelopen" && (
          <span
            data-testid={`past-badge-${event.id}`}
            className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-black/70 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-300 backdrop-blur"
          >
            Afgelopen
          </span>
        )}
        {isLive && (
          <span
            data-testid={`live-badge-${event.id}`}
            className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-[#FF3B30] px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-white"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
            </span>
            Nu live
          </span>
        )}
      </Link>

      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-display text-xl font-bold leading-tight tracking-tight">
          <Link to={detailUrl} data-testid={`event-link-${event.id}`} className="transition-colors hover:text-volt">
            {event.name}
          </Link>
        </h3>
        <div className="mt-2 space-y-1 text-sm text-slate-400">
          <p className="flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-slate-500" />
            {event.venue} · {event.city}
          </p>
          {event.event_date && (
            <p className="flex items-center gap-2">
              <CalendarDays className="h-3.5 w-3.5 shrink-0 text-slate-500" />
              {fmtRange(event.event_date, event.event_end)}
            </p>
          )}
        </div>

        <div className="mt-4 rounded-xl border border-white/[0.08] bg-black/40 p-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
            Kaartverkoop start
          </p>
          {isExpected ? (
            <p className="mt-1.5 font-mono text-sm text-slate-300">Datum wordt bekendgemaakt</p>
          ) : (
            <p className="mt-1.5 font-mono text-sm font-semibold text-volt">
              {fmtDate(event.sale_start, true)}
            </p>
          )}
          {event.status_note && (
            <p className="mt-1.5 font-mono text-[11px] font-medium uppercase tracking-wider text-blaze">
              {event.status_note}
            </p>
          )}
          {event.status === "binnenkort" && (
            <div className="mt-3">
              <Countdown target={event.sale_start} />
            </div>
          )}
          {isLive && (
            <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-blaze">
              <Zap className="h-3.5 w-3.5" />
              Verkoop is gestart — wees er snel bij
            </p>
          )}
        </div>

        {isLive && event.ticket_url && (
          <a
            href={event.ticket_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`buy-tickets-button-${event.id}`}
            className="mt-4 flex items-center justify-center gap-2 rounded-full bg-blaze px-4 py-2.5 text-sm font-bold text-black transition-transform hover:scale-[1.03] hover:bg-blaze-hover"
          >
            <Ticket className="h-4 w-4" />
            {event.button_text || "Koop tickets"}
            <ExternalLink className="h-3.5 w-3.5 opacity-60" />
          </a>
        )}

        <div className="mt-4 flex gap-2 pt-1">
          {gUrl ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid={`add-to-calendar-button-${event.id}`}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-white/15 px-3 py-2 text-xs font-semibold text-slate-200 transition-colors hover:border-volt/60 hover:text-volt"
                >
                  <CalendarPlus className="h-4 w-4" />
                  Agenda
                  <ChevronDown className="h-3 w-3 opacity-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="border-white/10 bg-[#12121C] text-slate-200">
                <DropdownMenuItem asChild>
                  <a
                    data-testid={`google-calendar-link-${event.id}`}
                    href={gUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="cursor-pointer"
                    onClick={() => toast.success("Google Agenda geopend")}
                  >
                    Google Agenda
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid={`apple-calendar-link-${event.id}`}
                  className="cursor-pointer"
                  onClick={() => {
                    downloadIcs(event);
                    toast.success("Agenda-bestand (.ics) gedownload");
                  }}
                >
                  Apple Agenda (.ics)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              disabled
              data-testid={`add-to-calendar-button-${event.id}`}
              className="flex flex-1 cursor-not-allowed items-center justify-center gap-1.5 rounded-full border border-white/10 px-3 py-2 text-xs font-semibold text-slate-600"
            >
              <CalendarPlus className="h-4 w-4" />
              Agenda
            </button>
          )}
          <button
            data-testid={`remind-me-button-${event.id}`}
            onClick={() => onRemind(event)}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-volt px-3 py-2 text-xs font-bold text-black transition-transform hover:scale-[1.04] hover:bg-volt-hover"
          >
            <BellRing className="h-4 w-4" />
            Herinner mij
          </button>
        </div>
      </div>
    </motion.article>
  );
};
